import { useState, useMemo, useCallback } from "react";
import { useIncome, type Income, type IncomeFilters as BackendFilters } from "@/hooks/admin/shared/useIncome";
import { supabase } from "@/integrations/supabase/client";
import useRolePermissions from "@/hooks/admin/shared/useRolePermissions";
import { normalizeText } from "@/utils/shared/stringUtils";
import { formatCurrency } from "@/utils/shared/currencyUtils";
import { formatFullNumericDateUTC } from "@/utils/shared/dateUtils";
import { useDebounce } from "@/hooks/useDebounce";

export const useIncomeFilters = () => {
  const { role } = useRolePermissions();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPaymentType, setSelectedPaymentType] = useState<Income["payment_type"] | "">("");
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Backend filters
  const backendFilters = useMemo<BackendFilters>(() => ({
    propertyIds: selectedProperties.length > 0 ? selectedProperties : undefined,
    paymentType: selectedPaymentType || undefined,
    dateFrom: dateFrom?.toISOString().split('T')[0],
    dateTo: dateTo?.toISOString().split('T')[0],
    searchTerm: debouncedSearchTerm || undefined,
    organizationIds: selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
  }), [selectedProperties, selectedPaymentType, dateFrom, dateTo, debouncedSearchTerm, selectedOrganizations]);

  const { data: paginatedData, isLoading, error, refetch } = useIncome(
    backendFilters,
    { page: currentPage, pageSize, orderBy: 'payout_date', orderDirection: 'desc' }
  );

  const paymentTypes: Income["payment_type"][] = [
    "Transferencia",
    "Efectivo"
  ];

  // Backend handles all filtering now
  const filteredIncomes = paginatedData?.data || [];

  const hasActiveFilters = !!(
    searchTerm ||
    selectedProperties.length > 0 ||
    selectedOrganizations.length > 0 ||
    selectedPaymentType ||
    dateFrom ||
    dateTo
  );

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedProperties([]);
    setSelectedOrganizations([]);
    setSelectedPaymentType("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setCurrentPage(1);
  };

  // Pagination handlers
  const goToPage = (page: number) => setCurrentPage(page);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, paginatedData?.totalPages || 1));
  const prevPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const changePageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  const handleSetSelectedProperties = (properties: string[]) => {
    setSelectedProperties(properties);
    setCurrentPage(1);
  };

  const handleSetSelectedOrganizations = (organizations: string[]) => {
    setSelectedOrganizations(organizations);
    setCurrentPage(1);
  };

  const handleSetSelectedPaymentType = (type: Income["payment_type"] | "") => {
    setSelectedPaymentType(type);
    setCurrentPage(1);
  };

  const handleSetDateFrom = (date: Date | undefined) => {
    setDateFrom(date);
    setCurrentPage(1);
  };

  const handleSetDateTo = (date: Date | undefined) => {
    setDateTo(date);
    setCurrentPage(1);
  };

  // Fetch all filtered incomes for CSV export (bypasses pagination)
  const fetchAllFilteredIncomes = useCallback(async (): Promise<Income[]> => {
    const { data, error } = await supabase.rpc("search_property_income", {
      p_search_term: backendFilters.searchTerm || null,
      p_property_ids: backendFilters.propertyIds || null,
      p_payment_type: backendFilters.paymentType || null,
      p_date_from: backendFilters.dateFrom || null,
      p_date_to: backendFilters.dateTo || null,
      p_page: 1,
      p_page_size: 10000,
      p_order_by: 'payout_date',
      p_order_direction: 'desc',
      p_organization_ids: backendFilters.organizationIds || null,
    });

    if (error) {
      console.error("Error fetching all filtered incomes:", error);
      throw error;
    }

    return (data || []) as Income[];
  }, [backendFilters]);

  return {
    // Data
    filteredIncomes,
    paymentTypes,
    
    // Loading states
    isLoading,
    error,
    refetch,
    
    // Pagination
    currentPage,
    pageSize,
    totalCount: paginatedData?.totalCount || 0,
    totalPages: paginatedData?.totalPages || 1,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    
    // Filter states
    searchTerm,
    selectedProperties,
    selectedOrganizations,
    selectedPaymentType,
    dateFrom,
    dateTo,
    hasActiveFilters,
    
    // Filter setters
    setSearchTerm,
    setSelectedProperties: handleSetSelectedProperties,
    setSelectedOrganizations: handleSetSelectedOrganizations,
    setSelectedPaymentType: handleSetSelectedPaymentType,
    setDateFrom: handleSetDateFrom,
    setDateTo: handleSetDateTo,
    clearFilters,
    
    // Export
    fetchAllFilteredIncomes,
  };
};